"use client";

import { useEffect, useState } from "react";
import { cn, Modal as UIModal, Text } from "@dionatanbig2be/big2be-ui";

/**
 * Falso no primeiro commit, verdadeiro logo depois. O Modal da lib só descobre
 * se é mobile num efeito (`useMediaQuery` nasce `false`): montado JÁ aberto —
 * caso dos overlays carregados sob demanda no GlobalOverlays —, ele abre o
 * `<dialog>` de desktop, troca para o BottomSheet no render seguinte e essa
 * troca dispara `onClose`, fechando o modal no mesmo instante. Segurando o
 * `open` até depois do primeiro commit, a lib já monta sabendo o layout certo.
 */
function useAfterFirstCommit(): boolean {
    const [committed, setCommitted] = useState(false);
    useEffect(() => {
        // setState em callback de timer (regra set-state-in-effect).
        const id = setTimeout(() => setCommitted(true), 0);
        return () => clearTimeout(id);
    }, []);
    return committed;
}

interface ModalProps {
    open: boolean;
    onClose: () => void;
    title?: React.ReactNode;
    /** Cabeçalho customizado; quando informado substitui o Modal.Header padrão. */
    header?: React.ReactNode;
    ariaLabel?: string;
    /** Classes extras da caixa (ex.: "max-w-2xl" para modais largos). */
    boxClassName?: string;
    headerClassName?: string;
    bodyClassName?: string;
    rootClassName?: string;
    /** Mostra o X de fechar (padrão true). Passe false em modais obrigatórios. */
    showCloseButton?: boolean;
    /**
     * Repassado pro Modal da lib. `false` evita que o botão que abriu o
     * modal fique com visual "pressed" preso após o fechamento (o `<dialog>`
     * nativo devolve foco a ele, e o daisyUI estiliza `:focus-visible` como
     * pressed). Default `true` (comportamento padrão da lib).
     */
    restoreFocus?: boolean;
    children: React.ReactNode;
}

/**
 * Modal padrão do app — wrapper fino do Modal v2 do big2be-ui, que já traz
 * backdrop, Escape, focus trap, botão de fechar, bottom-sheet no mobile e o
 * tema do cliente. Controlado por estado externo: quem abre é dono do
 * open/onClose.
 *
 *   <Modal open={open} onClose={close} title="Autenticação">...</Modal>
 */
export function Modal({
    open,
    onClose,
    title,
    header,
    boxClassName,
    headerClassName,
    bodyClassName,
    rootClassName,
    showCloseButton = true,
    restoreFocus,
    children,
}: ModalProps) {
    const committed = useAfterFirstCommit();

    return (
        <div data-component="ECMModal" className={cn("contents", rootClassName)}>
            <UIModal open={open && committed} onClose={onClose} className={boxClassName} restoreFocus={restoreFocus}>
                {header ??
                    (showCloseButton ? (
                        <UIModal.Header title={title} className={headerClassName} />
                    ) : title ? (
                        // Sem X: o Header da lib sempre renderiza o botão de
                        // fechar, então modais obrigatórios montam só o título.
                        <div data-slot="header" className={cn("flex items-center px-5 pt-5 pb-3", headerClassName)}>
                            <Text variant="h3" className="m-0 text-base font-bold leading-none text-base-content">
                                {title}
                            </Text>
                        </div>
                    ) : null)}
                <UIModal.Body className={bodyClassName}>{children}</UIModal.Body>
            </UIModal>
        </div>
    );
}
